import React, { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { Link, useNavigate } from "react-router-dom";
import PaginationComponent from "../../../Components/PaginationComponent";
import { BsThreeDotsVertical } from "react-icons/bs";
import EquipmentDetailModal from "../../../Components/EquipmentDetailModal";
import equipmentService from "../../../Services/inventoryService";
import toast from "react-hot-toast";

// ─── Main Component ───────────────────────────────────────────────────────────
const Equipments = () => {
  const navigate = useNavigate();

  const [searchData, setSearchData]       = useState("");
  const [filteredData, setFilteredData]   = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const itemsPerPage                      = 10;
  const [currentPage, setCurrentPage]     = useState(1);
  const [loading, setLoading]             = useState(false);
  const [detailItem, setDetailItem]       = useState(null);
  const [records, setRecords]             = useState([]);

  // FETCH ALL RECORDS
  useEffect(() => {
    const fetchRecords = async () => {
      setLoading(true);
      try {
        const data = await equipmentService.fetchAll();
        setRecords(data.inventories);
      } catch (error) {
        toast.error("Failed to fetch equipment records.");
      } finally {
        setLoading(false);
      }
    };
    fetchRecords();
  }, []);

  // FILTER BY SEARCH
  useEffect(() => {
    const filtered =
      records?.filter((item) =>
        item?.name_of_equipment?.toLowerCase().includes(searchData.toLowerCase())
      ) || [];
    setFilteredData(filtered);
    setCurrentPage(1);
  }, [searchData, records]);

  // PAGINATION
  useEffect(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end   = start + itemsPerPage;
    setPaginatedData(filteredData.slice(start, end));
  }, [currentPage, filteredData]);

  // ── Conditional column visibility ─────────────────────────────────────────
  const showCostOfMaintenance  = filteredData.some((item) => item.cost_of_maintenance);
  const showDateOfCondemnation = filteredData.some((item) => item.date_of_condemnation);
  const showDateOfReplacement  = filteredData.some((item) => item.date_of_replacement);
  const showOtherRemarks       = filteredData.some((item) => item.other_relevant_remarks);

  // ── Table column definitions ──────────────────────────────────────────────
  const columns = [
    {
      key: "sr",
      label: "Sr No.",
      render: (item, index) => (currentPage - 1) * itemsPerPage + index + 1,
    },
    {
      key: "name_of_equipment",
      label: "Name of Equipment",
      render: (item) => (
        <span className="font-[500] text-gray-800 whitespace-nowrap">
          {item.name_of_equipment}
        </span>
      ),
    },
    {
      key: "date_of_purchase",
      label: "Date of Purchase",
      render: (item) =>
        item.date_of_purchase
          ? new Date(item.date_of_purchase).toLocaleDateString()
          : <span className="text-gray-300">—</span>,
    },
    {
      key: "cost_of_equipment",
      label: "Cost of Equipment",
      render: (item) =>
        item.cost_of_equipment
          ? `Rs. ${item.cost_of_equipment}`
          : <span className="text-gray-300">—</span>,
    },
    {
      key: "nameAndAddressOfSupplier",
      label: "Name & Address of Supplier",
      render: (item) => (
        <span className="max-w-[180px] block truncate" title={item.nameAndAddressOfSupplier}>
          {item.nameAndAddressOfSupplier || <span className="text-gray-300">—</span>}
        </span>
      ),
    },
    {
      key: "date_of_manufacture",
      label: "Date of Manufacture",
      render: (item) =>
        item.date_of_manufacture
          ? new Date(item.date_of_manufacture).toLocaleDateString()
          : <span className="text-gray-300">—</span>,
    },
    {
      key: "date_of_installation",
      label: "Date of Installation",
      render: (item) =>
        item.date_of_installation
          ? new Date(item.date_of_installation).toLocaleDateString()
          : <span className="text-gray-300">—</span>,
    },
    {
      key: "department_where_intalled",
      label: "Department Where Installed",
      render: (item) => item.department_where_intalled || <span className="text-gray-300">—</span>,
    },
    {
      key: "environmental_control",
      label: "Environmental Control",
      render: (item) => item.environmental_control || <span className="text-gray-300">—</span>,
    },
    {
      key: "spare_parts_inventory",
      label: "Spare Parts Inventory",
      render: (item) => item.spare_parts_inventory || <span className="text-gray-300">—</span>,
    },
    {
      key: "technical_manualCircuit_diagramLiterature",
      label: "Technical Manual / Circuit Diagrams",
      render: (item) => item.technical_manualCircuit_diagramLiterature || <span className="text-gray-300">—</span>,
    },
    {
      key: "after_sale_service_arrangement",
      label: "After Sale Service Arrangement",
      render: (item) => item.after_sale_service_arrangement || <span className="text-gray-300">—</span>,
    },
    {
      key: "warranty_period",
      label: "Warranty Period",
      render: (item) => item.warranty_period || <span className="text-gray-300">—</span>,
    },
    {
      key: "life_of_equipment",
      label: "Life of Equipment",
      render: (item) => item.life_of_equipment || <span className="text-gray-300">—</span>,
    },
    {
      key: "depreciation_per_year",
      label: "Depreciation Per Year",
      render: (item) =>
        item.depreciation_per_year
          ? `Rs. ${item.depreciation_per_year}`
          : <span className="text-gray-300">—</span>,
    },

    // ── Conditional columns ─────────────────────────────────────────────────
    {
      key: "cost_of_maintenance",
      label: "Cost of Maintenance",
      hidden: !showCostOfMaintenance,
      render: (item) =>
        item.cost_of_maintenance
          ? `Rs. ${item.cost_of_maintenance}`
          : <span className="text-gray-300">—</span>,
    },
    {
      key: "date_of_condemnation",
      label: "Date of Condemnation",
      hidden: !showDateOfCondemnation,
      render: (item) =>
        item.date_of_condemnation
          ? new Date(item.date_of_condemnation).toLocaleDateString()
          : <span className="text-gray-300">—</span>,
    },
    {
      key: "date_of_replacement",
      label: "Date of Replacement",
      hidden: !showDateOfReplacement,
      render: (item) =>
        item.date_of_replacement
          ? new Date(item.date_of_replacement).toLocaleDateString()
          : <span className="text-gray-300">—</span>,
    },
    {
      key: "other_relevant_remarks",
      label: "Other Relevant Remarks",
      hidden: !showOtherRemarks,
      render: (item) => (
        <span className="max-w-[200px] block truncate" title={item.other_relevant_remarks}>
          {item.other_relevant_remarks || <span className="text-gray-300">—</span>}
        </span>
      ),
    },

    // ── Action columns ──────────────────────────────────────────────────────
    {
      key: "update",
      label: "Update",
      render: (item) => (
        <button
          onClick={() =>
            navigate(`/account/equipment/update/${item?.id}`, {
              state: { equipment: item },
            })
          }
          className="bg-gray-800 text-white text-[11px] px-3 py-1.5 rounded-md hover:bg-gray-700 transition-colors whitespace-nowrap"
        >
          Update
        </button>
      ),
    },
    {
      key: "details",
      label: "Details",
      render: (item) => (
        <button
          onClick={() => setDetailItem(item)}
          className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
          title="View details"
        >
          <BsThreeDotsVertical size={16} />
        </button>
      ),
    },
  ];

  const visibleColumns = columns.filter((col) => !col.hidden);

  return (
    <div className="p-6 w-full max-w-[1400px]  mx-auto">

      {/* HEADER CARD */}
      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-6 py-4 mb-5 shadow-sm">
        <div>
          <h1 className="text-[17px] font-[500] text-gray-800">
            Equipment Maintenance Records
          </h1>
          <p className="text-[12px] text-gray-400 mt-0.5">
            {filteredData.length} records found
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <SearchIcon
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
              style={{ fontSize: 16 }}
            />
            <input
              type="search"
              placeholder="Search by equipment name..."
              value={searchData}
              onChange={(e) => setSearchData(e.target.value)}
              className="pl-8 pr-3 py-[7px] w-[240px] text-[13px] bg-gray-50 border border-gray-200 rounded-lg focus:outline-none"
            />
          </div>

          {/* Add Equipment */}
          <Link to="add">
            <button className="bg-gray-800 text-white text-[13px] px-4 py-[7px] rounded-lg whitespace-nowrap">
              + Add Equipment
            </button>
          </Link>
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden w-full">
        <div className="overflow-x-auto overflow-y-auto max-h-[60vh]">
          <table className="w-max min-w-full border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-gray-800 text-[11px] uppercase text-white">
                {visibleColumns.map((col) => (
                  <th
                    key={col.key}
                    className="py-3 px-4 text-left whitespace-nowrap border-gray-700"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={visibleColumns.length}
                    className="py-10 text-center text-[13px] text-gray-400"
                  >
                    Loading equipment records...
                  </td>
                </tr>
              ) : paginatedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={visibleColumns.length}
                    className="py-10 text-center text-[13px] text-gray-400"
                  >
                    No equipment records found.
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr
                    key={item?.id}
                    className="border-t border-gray-100 hover:bg-gray-50 text-[13px] text-gray-700"
                  >
                    {visibleColumns.map((col) => (
                      <td
                        key={col.key}
                        className={`py-3 px-4 border-gray-100 last:border-r-0 ${
                          col.key === "update" || col.key === "details"
                            ? "text-center"
                            : ""
                        }`}
                      >
                        {col.render(item, index)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PAGINATION */}
      <PaginationComponent
        filteredData={filteredData}
        setPaginatedData={setPaginatedData}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />

      {/* DETAIL MODAL */}
      {detailItem && (
        <EquipmentDetailModal
          item={detailItem}
          onClose={() => setDetailItem(null)}
        />
      )}
    </div>
  );
};

export default Equipments;